import { SearchFacetInput, SortField } from '__generated__/graphql'
import React, { createContext, ReactNode, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'

export interface QueryParamsContextValue {
    searchQueryParams: SearchFacetInput[]
    sortFieldParams: SortField[]
    handleSetSearchQueryParams: (newSearch: SearchFacetInput[]) => void
    handleSetSortFieldParams: (newSort: SortField[]) => void
}

export const QueryParamsContext = createContext<QueryParamsContextValue>(
    {} as QueryParamsContextValue
)

const SEARCH_QUERY_PARAM_NAME = 'searchQuery'
const SORT_FIELD_QUERY_PARAM_NAME = 'sortField'

interface QueryParamsProviderProps {
    children: ReactNode
    /** Optional prefix to add to localStorage keys to prevent collisions between different contexts */
    prefix?: string
}

export const QueryParamsProvider: React.FC<QueryParamsProviderProps> = ({
    children,
    prefix = '',
}) => {
    const toast = useToast()

    // Create prefixed localStorage keys
    const searchKey = prefix
        ? `${prefix}_${SEARCH_QUERY_PARAM_NAME}`
        : SEARCH_QUERY_PARAM_NAME
    const sortKey = prefix
        ? `${prefix}_${SORT_FIELD_QUERY_PARAM_NAME}`
        : SORT_FIELD_QUERY_PARAM_NAME

    const [searchQueryParams, setSearchQueryParams] = useState<
        SearchFacetInput[]
    >(() => {
        const jsonString = localStorage.getItem(searchKey)

        if (!jsonString) return []

        let parsedSearchQueryParams: SearchFacetInput[] = []

        try {
            parsedSearchQueryParams = JSON.parse(jsonString)

            return parsedSearchQueryParams
        } catch (e) {
            console.error('Failed to parse search query params', e)
            toast({
                message: 'Failed to parse search query params',
                title: 'Something went wrong',
            })
            return []
        }
    })

    // TODO AH determine if we want to include this
    const [sortFieldParams, setSortFieldParams] = useState<SortField[]>(() => {
        const jsonString = localStorage.getItem(sortKey)

        if (!jsonString) return []

        let parsedSortFieldParams: SortField[] = []

        try {
            parsedSortFieldParams = JSON.parse(jsonString)

            return parsedSortFieldParams
        } catch (e) {
            console.error('Failed to parse sort field query params', e)

            toast({
                message: 'Failed to parse sort field query params',
                title: 'Something went wrong',
            })

            return []
        }
    })

    const handleSetSearchQueryParams = (newSearch: SearchFacetInput[]) => {
        try {
            localStorage.setItem(searchKey, JSON.stringify(newSearch))
            setSearchQueryParams(newSearch)
        } catch (e) {
            console.error('Failed to save search', e)
        }
    }

    const handleSetSortFieldParams = (newSort: SortField[]) => {
        try {
            localStorage.setItem(sortKey, JSON.stringify(newSort))
            setSortFieldParams(newSort)
        } catch (e) {
            console.error('Failed to save search', e)
        }
    }

    return (
        <QueryParamsContext.Provider
            value={{
                searchQueryParams,
                handleSetSearchQueryParams,
                sortFieldParams,
                handleSetSortFieldParams,
            }}
        >
            {children}
        </QueryParamsContext.Provider>
    )
}
